// The English texts (the area files' Texts sections), the fallback for every other language.
// {product} is the product's name; {n} picks a plural form where a text has two.

export const en = {
  // 02 Shared
  'common.cancel': 'Cancel',
  'common.retry': 'Retry',
  'common.reload': 'Reload',
  'common.retryNow': 'Retry now',
  'common.goHome': 'Go to Home',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.change': 'Change',
  'common.changeAvatar': 'Change avatar',
  'common.later': 'Later',
  'common.tryAgain': 'Try again',
  'field.yourPassword': 'Your password',
  'field.typeToConfirm': 'Type the address to confirm',
  'wait.tooSlow': 'This is taking too long. Check your connection and try again.',
  'wait.noAnswer': 'No answer yet. Check your connection — it may still go through, so look before trying again.',
  'wait.reference': 'Reference: {code}',
  'connection.reconnecting': 'Connection lost. Reconnecting…',
  'connection.lost': 'Connection lost. Check your internet connection.',

  // 02 Error wording
  'error.network': 'Couldn’t connect. Check your connection and try again.',
  'error.unavailable': 'The server isn’t answering right now. Try again in a moment.',
  'error.unavailableCall': 'Calls aren’t available right now. Try again in a moment.',
  'error.waitSeconds': { one: 'Too many tries. Wait {n} second and try again.', other: 'Too many tries. Wait {n} seconds and try again.' },
  'error.waitMinutes': { one: 'Too many tries. Wait {n} minute and try again.', other: 'Too many tries. Wait {n} minutes and try again.' },
  'error.waitMoment': 'Too many tries. Wait a moment and try again.',
  'error.signedOut': 'You’ve been signed out. Please sign in again.',
  'error.mailSignedOut': 'Your circle account needs your password again on this device.',
  'error.forbidden': 'You don’t have permission to do that.',
  'error.noAccess': 'You don’t have access to this.',
  'error.forbiddenCall': 'You can’t join this call — you’re not in this channel.',
  'error.notFound': 'That couldn’t be found. It may have been removed.',
  'error.notFoundLoad': 'This couldn’t be found. It may have been removed.',
  'error.conflict': 'This was changed somewhere else in the meantime. Try again.',
  'error.exists': 'There’s already one with that name. Choose another name.',
  'error.notEmpty': 'This folder isn’t empty. Move or delete what’s in it first.',
  'error.badAddress': 'One of the addresses isn’t right. Check them and try again.',
  'error.tooLarge': 'That’s too large.',
  'error.fileTooLarge': 'This file is too large.',
  'error.noSpace': 'There’s no space left. Delete something and try again.',
  'error.deviceRefused': '{product} isn’t allowed to use your camera or microphone. Allow it in your browser’s site settings (the icon next to the address), then try again.',
  'error.noDevice': 'No camera or microphone was found. Connect one and try again.',
  'error.deviceBusy': 'Your camera or microphone is being used by another app. Close it and try again.',
  'error.send': 'Couldn’t send it. Try again.',
  'error.upload': 'Couldn’t upload it. Try again.',
  'error.search': 'Couldn’t search right now. Try again.',
  'error.load': 'Couldn’t load this. Try again.',
  'error.account': 'The server couldn’t do that. Try again.',
  'error.call': 'The call couldn’t start. Try again.',
  'error.save': 'Couldn’t save it. Try again.',
  'error.general': 'Something went wrong. Try again.',
  'error.joinInviteOnly': 'This channel is invite-only.',
  'error.joinNetwork': 'Couldn’t connect. Try again.',
  'error.joinOther': 'This channel doesn’t exist or you can’t join it.',
  'error.joinSpaceInviteOnly': 'This space is invite-only.',
  'error.joinSpaceOther': 'This space doesn’t exist or you can’t join it.',

  // 02 Times and dates (the numbers and names are Intl's)
  'time.todayAt': 'Today at {time}',
  'time.yesterdayAt': 'Yesterday at {time}',
  'time.lastWeekdayAt': 'Last {weekday} at {time}',
  'time.today': 'Today',
  'time.yesterday': 'Yesterday',
  'time.dateTime': '{date}, {time}',

  // 03 Start screens
  'start.connecting': 'Connecting…',
  'start.opening': 'Opening your chats…',
  'start.openingChats': 'Opening your chats',
  'start.slow': 'This is taking longer than usual. Check your connection, or try again.',
  'start.clearCache': 'Clear cached chats and reload',
  'start.unreachable': '{product} can’t reach your circle’s server. Check your connection — {product} keeps trying.',
  'start.configFailed': '{product} couldn’t load its settings. Check your connection and try again.',
  'start.offline': 'You’re offline.',
  'start.offlineLine': '{product} opens as soon as you’re back online.',
  'start.stillOffline': 'Still offline…',
  'start.cantStart': '{product} couldn’t start',
  'start.partMissing': 'A part of {product} couldn’t be loaded — maybe it was just updated. Reload to go on.',
  'start.cantReach': '{product} can’t reach your circle’s server. Check your connection and try again.',
  'start.wentWrongStarting': 'Something went wrong while starting. Trying again usually helps.',
  'start.startFresh': 'Sign out and start fresh…',
  'start.startFreshWarning': 'Signing out deletes this device’s copy of your chats. Encrypted messages can only be read again with your recovery key. Only do this if trying again keeps failing.',
  'start.signOut': 'Sign out',
  'start.wentWrong': 'Something went wrong',
  'start.wentWrongLine': 'Something went wrong. Trying again usually helps.',
  'start.signedOut': 'You’ve been signed out',
  'start.signedOutLine': 'This device’s sign-in ended — maybe you signed out on another device, or it was removed from your sessions. Sign in again to go on.',
  'start.signInAgain': 'Sign in again',
  'start.noStorage': '{product} can’t store its data in this browser',
  'start.noStorageLine': 'This browser doesn’t let {product} keep data on this device — private windows often don’t. Open {product} in a normal window, or allow this site to store data.',

  // 04 Sign-in and sign-out
  'signin.tabTitle': 'Sign in — {product}',
  'signin.title': 'Sign in to {product}',
  'signin.sub': 'With your work email.',
  'signin.workEmail': 'Work email',
  'signin.continue': 'Continue',
  'signin.newHere': 'New to {product}? The person who looks after it in your circle invites you.',
  'signin.enterPassword': 'Enter your password',
  'signin.signingInAs': 'Signing in as',
  'signin.password': 'Password',
  'signin.forgot': 'Forgot password?',
  'signin.signIn': 'Sign in',
  'signin.emptyPassword': 'Please enter your password.',
  'signin.wrongPassword': 'That password isn’t right. Try again, or reset it.',
  'signin.show': 'Show',
  'signin.hide': 'Hide',
  'signin.enterCode': 'Enter your code',
  'signin.codeSub': 'Open your authenticator app and enter the six-digit code for {product}.',
  'signin.code': 'Code from your authenticator app',
  'signin.confirm': 'Confirm',
  'signin.recoveryCode': 'Use a recovery code instead',
  'signin.sixDigits': 'Please enter the 6-digit code.',
  'signin.wrongCode': 'That code didn’t work. Enter the newest code from your authenticator app.',
  'signin.failedNow': 'Signing in didn’t work right now',
  'signin.ourSide': 'It’s on our side, not yours. Please try again in a minute.',
  'signin.keepsHappening': 'If it keeps happening, tell the person who looks after {product} in your circle.',
  'signin.private': 'Private by design · Hosted in Switzerland',
  'signin.privacy': 'Privacy',
  'signin.terms': 'Terms',
  'signin.imprint': 'Imprint',
  'signin.logo': '{product} logo',
  'signin.finding': 'Finding your circle…',
  'signin.notAnAddress': 'Please enter your work email address, like name@company.com.',
  'signin.unknownCompany': '{product} doesn’t know this circle yet. Please check the address.',
  'signin.pilot': 'Ask for a {product} pilot for your company',
  'signin.busyMinutes': { one: 'Too many tries. Please try again in a minute.', other: 'Too many tries. Please try again in {n} minutes.' },
  'signin.serviceDown': 'The service isn’t answering right now. Please try again in a moment.',
  'signin.notPossible': 'Signing in isn’t possible right now. Please try again in a moment.',
  'signin.anotherEmail': 'Use another email address',
  'signin.connecting': 'Connecting…',
  'signin.connectingCompany': 'Connecting to your circle…',
  'signin.misconfigured': 'This server isn’t set up correctly. Please tell your circle’s admin.',
  'signin.unreachable': 'This server can’t be reached right now. Please try again in a moment.',
  'signin.unusable': 'This server can’t be found. Please check the address.',
  'signin.flows': 'Signing in to this server isn’t possible right now. Try again in a moment.',
  'signin.failedMoment': 'Signing in didn’t work right now. Please try again in a moment.',
  'signin.notSetUp': 'This {product} isn’t set up yet',
  'signin.notSetUpLine': 'No company server has been added to it so far. If you look after it, add your company’s server to its settings (config.json).',
  'signin.username': 'Username',
  'signin.nothingUsable': 'Signing in to this circle isn’t possible with {product}.',
  'signin.checkTyped': 'Signing in didn’t work. Please check what you typed.',
  'signin.waitMoment': 'Too many tries. Please wait a moment and try again.',
  'signin.deactivated': 'This account is no longer active. Ask your circle’s admin.',
  'signin.wrongUser': 'Wrong username or password.',
  'signin.tooLong': 'Signing in is taking too long. Check your connection and try again.',
  'signin.didntWork': 'Signing in didn’t work',
  'signin.linkExpired': 'This sign-in link has expired. Please sign in again.',
  'signin.deviceBrowser': '{product} on {device} ({browser})',
  'signin.deviceApp': '{product} app on {device}',
  'signin.device': '{product} on {device}',
  'signin.browser': '{product} ({browser})',
  'signin.web': '{product} Web',
  'signout.title': 'Sign out',
  'signout.sure': 'You’re about to sign out. Are you sure?',
  'signout.busy': 'Signing out…',
  'signout.failed': 'Couldn’t sign out. {why}',
  'signout.noKey': 'Create a recovery key first',
  'signout.noKeyLine': 'Without it you can’t read your earlier messages after you sign in again. It takes a minute.',
  'signout.createKey': 'Create recovery key',
  'signout.unconfirmed': 'This device isn’t confirmed yet',
  'signout.unconfirmedLine': 'Confirm it with your recovery key in Settings → Sessions before you sign out, so you don’t lose messages that only this device can read.',

  // 07 The frame
  'frame.home': 'Home',
  'frame.direct': 'Direct messages',
  'frame.search': 'Search',
  'frame.addSpace': 'Add Space',
  'frame.settings': 'Settings',
  'frame.searchMessages': 'Search messages',
  'frame.saved': 'Saved messages',
  'frame.panelTile': '{circle}, spaces and channels',
  'frame.mentions': 'Mentions',
  'frame.invites': 'Invites',
  'frame.channels': 'Channels',
  'frame.createChannel': 'Create channel',
  'frame.newMessage': 'New message',
  'frame.chats': 'Chats',
  'frame.voice': 'Voice',
  'notice.reacted': '{name} reacted {emoji}',
  'notice.reactedTwo': '{name} and {other} reacted {emoji}',
  'notice.reactedMany': '{name} and {n} others reacted {emoji}',
  'notice.open': ', open the message',
  'frame.unreadCount': '{n} unread',
  'frame.mentionCount': { one: '1 mention', other: '{n} mentions' },
  'frame.unread': 'unread',
  'frame.namesTwo': '{a} and {b}',
  'frame.namesThree': '{a}, {b} and {c}',
  'frame.namesMore': '{a}, {b}, {c} and {n} more',
  'frame.mention': 'mention',
  'frame.noMessages': 'No messages yet',
  'frame.welcomeTo': 'Welcome to {circle}',
  'frame.startWith': 'Start with one of these.',
  'frame.createAChannel': 'Create a channel',
  'frame.createAChannelLine': 'A place for a topic, a team or a project.',
  'frame.invitePeopleLine': 'Bring your colleagues into {circle}.',
  'frame.openSpace': 'Open a space',
  'frame.openSpaceLine': 'Join one of {circle}’s spaces.',
  'frame.findPeople': 'Find people',
  'frame.findPeopleLine': 'Write to someone in {circle}.',
  'frame.startChat': 'Start a chat',
  'frame.pickSomeone': 'Pick someone from {circle}.',
  'frame.nobodyHere': 'Nobody else is here yet.',
  'frame.markRead': 'Mark as read',
  'frame.copyLink': 'Copy link',
  'frame.leave': 'Leave',
  'frame.leaveSpace': 'Leave space',
  'frame.lobby': 'Lobby',
  'frame.circleMenu': 'Circle menu',
  'frame.circleMenuOf': '{circle}, Circle menu',
  'frame.circleSettings': 'Circle settings',
  'frame.ctrlK': 'Ctrl K',
  'frame.spaceMenu': 'Space menu',
  'frame.moreOptions': 'More options',
  'frame.leaveTitle': 'Leave {name}?',
  'frame.leaveChannelTitle': 'Leave this channel?',
  'frame.leaveSpaceTitle': 'Leave this space?',
  'frame.leaveText': 'You can come back if someone invites you again or the channel is open to you.',
  'frame.leaveTextSpace': 'You can come back if someone invites you again or the space is open to you.',
  'frame.leaving': 'Leaving…',
  'frame.cantLeave': 'Couldn’t leave. ',
  'frame.hideList': 'Hide channel list',
  'frame.showList': 'Show channel list',
  'frame.tagline': '{circle} team chat',
  'frame.startConversation': 'Start a conversation',
  'frame.startConversationLine': 'Message someone from your team directly.',
  'frame.createSpace': 'Create a space',
  'frame.createSpaceLine': 'A home for a team or a project, with its own channels.',
  'frame.searchLine': 'Find anything said in your chats. Press / any time.',
  'frame.makeYours': 'Make it yours',
  'frame.makeYoursLine': 'Colours, notifications, voice & video and more in Settings.',
  'frame.channelPageHeading': 'A place for your team to talk',
  'frame.spacePageTitle': 'Create space',
  'frame.spacePageText': 'Its own channels, for a team or a project.',
  'frame.writeTo': 'Who do you want to write to?',

  // 08 The lobby
  'lobby.channels': 'Channels',
  'lobby.addChannel': 'Add channel',
  'lobby.addExisting': 'Add existing channel',
  'lobby.unknown': 'Unknown',
  'lobby.inaccessible': 'Inaccessible',
  'lobby.cantJoin': 'Couldn’t join',
  'lobby.members': { one: '{n} member', other: '{n} members' },
  'lobby.open': 'Open channel',
  'lobby.join': 'Join',
  'lobby.joining': 'Joining…',
  'lobby.noDetails': 'Details not available',
  'lobby.noChannels': 'No channels',
  'lobby.noChannelsLine': 'This space has no channels yet.',
  'lobby.remove': 'Remove from space',
  'lobby.moveUp': 'Move up',
  'lobby.moveDown': 'Move down',
  'lobby.didntWork': 'That didn’t work. {reason}',
  'lobby.findChannel': 'Find a channel',
  'lobby.allIn': 'All your channels are in this space.',
  'lobby.add': 'Add',
  'lobby.addN': 'Add {n}',
  'lobby.adding': 'Adding…',
  'lobby.addFailed': 'The channels couldn’t be added. Please try again.',
  'lobby.reordering': 'Reordering',
  'lobby.orderFailed': 'Couldn’t save the new order. {reason}',

  // 10 Timeline, 13 Messages, 17's membership lines
  'chat.pinned': 'Pinned messages',
  'chat.isTyping': '{names} is typing…',
  'chat.areTyping': '{names} are typing…',
  'chat.cantPost': 'You can’t post here',
  'chat.open': 'Open',
  'chat.unpin': 'Unpin',
  'chat.cantShowHere': 'This message can’t be shown here.',
  'chat.cantLoadPinned': 'This message couldn’t be loaded.',
  'chat.noPinned': 'No pinned messages',
  'chat.noPinnedLine': 'To pin a message, use its menu (⋮) — if you’re allowed to.',
  'chat.messages': 'Messages',
  'chat.newMessages': 'New messages',
  'chat.jumpToLatest': 'Jump to latest',
  'chat.newPill': '↓ {n} new',
  'chat.newPillName': { one: '{n} new message, jump to latest', other: '{n} new messages, jump to latest' },
  'chat.olderFailed': 'Couldn’t load older messages.',
  'chat.newerFailed': 'Couldn’t load newer messages.',
  'chat.createdBy': 'Created by {name} on {date} {time}',
  'chat.startsHere': 'The conversation starts here.',
  'chat.startsWith': 'Your conversation with {name} starts here.',
  'chat.messageGone': 'That message isn’t available any more.',
  'chat.reply': 'Reply',
  'chat.edit': 'Edit',
  'chat.messageOptions': 'Message options',
  'chat.editMessage': 'Edit message',
  'chat.viewReactions': 'View reactions',
  'chat.readReceipts': 'Read receipts',
  'chat.pinMessage': 'Pin message',
  'chat.unpinMessage': 'Unpin message',
  'chat.delete': 'Delete',
  'chat.report': 'Report',
  'chat.edited': '(edited)',
  'chat.changedName': '{a} changed the name',
  'chat.changedTopic': '{a} changed the topic',
  'chat.changedPicture': '{a} changed the picture',
  'chat.startedCall': '{a} started a call',
  'chat.calledNoAnswer': '{a} called · no answer',
  'chat.ongoing': 'ongoing',
  'chat.with': 'with',
  'chat.underMinute': '< 1 min',
  'chat.minutes': '{n} min',
  'chat.hoursMinutes': '{h} h {m} min',
  'chat.deleted': 'Message deleted',
  'chat.deletedBecause': 'Message deleted. {reason}',
  'chat.opening': 'Opening this message…',
  'chat.cantRead': 'This message can’t be read here',
  'chat.cantReadUnlock': 'To read it, unlock this device with your recovery key in Settings → Sessions',
  'chat.cantReadNoKey': 'Messages from before you signed in here can’t be opened on this device without a recovery key',
  'chat.unsupported': 'This kind of message can’t be shown here',
  'chat.broken': 'This message can’t be shown',
  'chat.couldntLoad': 'This message couldn’t be loaded',
  'chat.aChannel': 'a channel',
  'chat.messageIn': 'Message: {channel}',
  'chat.expand': 'Expand',
  'chat.collapse': 'Collapse',
  'chat.view': 'View',
  'chat.watch': 'Watch',
  'chat.pictureFailed': 'This picture couldn’t be loaded.',
  'chat.videoFailed': 'This video couldn’t be loaded.',
  'chat.play': 'Play',
  'chat.openPdf': 'Open PDF',
  'chat.download': 'Download ({size})',
  'chat.retryDownload': 'Retry download ({size})',
  'chat.fileFailed': 'This file couldn’t be loaded.',
  'chat.unnamedFile': 'Unnamed file',
  'chat.reactedWith': { one: '{names} reacted with {emoji}', other: '{names} reacted with {emoji}' },
  'chat.andOthers': { one: 'and 1 other', other: 'and {n} others' },
  'chat.reactedTitle': 'Reacted with {emoji}',
  'chat.reactionNotAdded': 'Your reaction couldn’t be added.',
  'chat.reactionNotTaken': 'Your reaction couldn’t be taken back.',
  'chat.tryAgain': 'Try again.',
  'chat.seenBy': 'Seen by',
  'chat.nobodyRead': 'Nobody has read it yet.',
  'chat.deleteTitle': 'Delete message',
  'chat.deleteText': 'This can’t be undone. Are you sure you want to delete this message?',
  'chat.reasonOptional': 'Reason (optional)',
  'chat.deleting': 'Deleting…',
  'chat.reportTitle': 'Report message',
  'chat.reportText': 'Report this message to the server, which may then notify the people who look after it.',
  'chat.reason': 'Reason',
  'chat.reasonNeeded': 'Please say why you report it.',
  'chat.reporting': 'Reporting…',
  'chat.reported': 'Message reported. Thank you.',
  'chat.linkCopied': 'Link copied',
  'chat.pinFailed': 'The message couldn’t be pinned.',
  'chat.unpinFailed': 'The message couldn’t be unpinned.',
  'chat.notSent': 'Not sent.',
  'chat.invited': '{a} invited {b}',
  'chat.joined': '{b} joined',
  'chat.declined': '{b} declined the invite',
  'chat.withdrew': '{a} withdrew the invite for {b}',
  'chat.unbanned': '{a} unbanned {b}',
  'chat.left': '{b} left',
  'chat.removed': '{a} removed {b}',
  'chat.banned': '{a} banned {b}',
  'chat.renamed': '{old} is now called {new}',
  'chat.nameRemoved': '{b} removed their name',
  'chat.pictureChanged': '{b} changed their picture',
  'chat.pictureRemoved': '{b} removed their picture',
  'chat.membershipUpdated': 'Membership updated',

  // 15 The message box
  'box.placeholder': 'Message {name}',
  'box.editPlaceholder': 'Edit message…',
  'box.attach': 'Attach a file',
  'box.add': 'Add',
  'box.send': 'Send',
  'box.cancelReply': 'Cancel reply',
  'box.save': 'Save',
  'box.mentions': 'Mentions',
  'box.channels': 'Channels',
  'box.commands': 'Commands',
  'box.noOne': 'No one here by that name',
  'box.noChannel': 'No channel by that name',
  'box.meLine': 'Send action message',
  'box.emoticonLine': 'Send {emoticon} as message',
  'box.unknownTitle': 'Unknown command',
  'box.unknownText': '{product} has no “{word}” command. Send your message as text?',
  'box.sendAsText': 'Send as text',
  'box.dropIn': 'Drop files in “{name}”',
  'box.dropHere': 'Drop files in this room',
  'box.dropLine': 'They are added to your message.',
  'box.files': 'Files',
  'box.remove': 'Remove',
  'box.removeAll': 'Remove all',
  'box.retryUpload': 'Retry upload',
  'box.cancelUpload': 'Cancel upload',
  'box.tooBig': 'The file is too big: at most {limit}, and this one is {size}.',
  'box.editFailed': 'Your change couldn’t be saved.',

  // 12 Notifications
  'notify.default': 'Default',
  'notify.mentions': 'Mentions & keywords',
  'notify.mute': 'Mute',
  'notify.namedMute': 'Notifications: muted',
  'notify.namedAll': 'Notifications: all messages',
  'notify.namedMentions': 'Notifications: mentions and keywords',
  'notify.newMessage': 'New message from {name}',

  // 09 Create
  'create.channel': 'Create channel',
  'create.channelName': 'Channel name',
  'create.type': 'Channel type',
  'create.text': 'Text channel',
  'create.voice': 'Voice channel',
  'create.create': 'Create',
  'create.creating': 'Creating…',
  'create.failed': 'Couldn’t create the channel.',
  'create.name': 'Name',
  'create.topicOptional': 'Topic (optional)',
  'create.topic': 'Topic',
  'create.options': 'Options',
  'create.encryption': 'End-to-end encryption',
  'create.encryptionText': 'Only the people in the channel can read its messages. It can’t be switched off later.',
  'create.notLinked': 'The channel was made but couldn’t be added to the space.',
  'create.addToSpace': 'Add it to the space',
  // 09 A space's access and first channel, 07 "Spaces in <Circle>" (v1.58.13)
  'create.access': 'Access',
  'create.everyone': 'Everyone in {circle}',
  'create.everyoneLine': 'Everyone in {circle} can see it and join.',
  'create.private': 'Private',
  'create.privateLine': 'Only people who are invited can join.',
  'create.public': 'Public',
  'create.publicLine': 'Anyone who has its address can join.',
  'create.general': 'General',
  'frame.circleSpaces': 'Spaces in {circle}',
  'frame.joining': 'Joining…',
  'frame.cantJoin': 'Couldn’t join. {reason}',
  'create.generalFailed': 'Couldn’t create the General channel. {reason}',

  // 14 Viewers and joining
  'view.viewer': 'Viewer',
  'view.close': 'Close viewer',
  'view.zoomOut': 'Zoom out',
  'view.zoomIn': 'Zoom in',
  'view.download': 'Download',
  'view.copyText': 'Copy text',
  'chat.openFile': 'Open file',
  'join.join': 'Join',
  'join.joining': 'Joining…',
  'join.space': 'Space',
  'join.channel': 'Channel',
  'join.members': { one: '{count} member', other: '{count} members' },
  'join.nowhere': 'This link doesn’t lead anywhere you can open.',
  'join.left': 'You left this channel.',
  'join.askAgain': 'To come back, ask someone in it to invite you again.',
  'join.removed': 'You were removed from this channel.',
  'join.otherCompany': 'This channel belongs to another circle. It can’t be opened from here.',
  'join.invitedYou': '{name} invited you',
  'join.wantsToChat': '{name} wants to chat',
  'join.accept': 'Accept',
  'join.decline': 'Decline',

  // 16 Emoji and packs
  'box.emoji': 'Emoji',
  'box.keyboard': 'Keyboard',
  'box.emojis': 'Emojis',
  'chat.addReaction': 'Add reaction',
  'emoji.search': 'Search',
  'emoji.searchName': 'Search emoji',
  'emoji.tone': 'Skin tone',
  'emoji.noTone': 'No skin tone',
  'emoji.light': 'Light',
  'emoji.mediumLight': 'Medium-light',
  'emoji.medium': 'Medium',
  'emoji.mediumDark': 'Medium-dark',
  'emoji.dark': 'Dark',
  'emoji.recent': 'Recent',
  'emoji.results': 'Search results',
  'emoji.noResults': 'No results',
  'emoji.smileys': 'Smileys & People',
  'emoji.animals': 'Animals & Nature',
  'emoji.food': 'Food & Drinks',
  'emoji.activity': 'Activity',
  'emoji.travel': 'Travel & Places',
  'emoji.objects': 'Objects',
  'emoji.symbols': 'Symbols',
  'emoji.flags': 'Flags',
  'emoji.unknownPack': 'Unknown pack',
  'emoji.companyEmoji': 'Circle emoji',
  'emoji.cellName': '{name} emoji',
  'frame.companyEmoji': 'Circle emoji',
  'frame.companyEmojiLine': 'This only keeps your circle’s emoji and stickers — there’s nothing to read here. You find them in the emoji picker.',

  // 17 People
  'people.searchPlaceholder': 'Search people and messages…',
  'people.searchName': 'Search people and messages',
  'people.membersOnline': { one: '1 member online', other: '{n} members online' },
  'people.onlineGroup': 'Online – {n}',
  'people.offlineGroup': 'Offline – {n}',
  'people.noMembers': 'No members',
  'people.scrollTop': 'Scroll to top',
  'people.showMembers': 'Show members',
  'people.hideMembers': 'Hide members',
  'people.person': 'Person',
  'people.picture': '{name}’s picture',
  'people.online': 'Online',
  'people.idle': 'Idle',
  'people.dnd': 'Do Not Disturb',
  'people.offline': 'Offline',
  'threads.replies': { one: '1 reply', other: '{n} replies' },
  'threads.new': ', new',
  'threads.lastReply': 'Last reply {when}',
  'threads.justNow': 'just now',
  'threads.minAgo': '{n} min ago',
  'threads.hAgo': '{n} h ago',
  'threads.yesterday': 'yesterday',
  'threads.newReplies': 'New replies',
  'threads.thread': 'Thread',
  'threads.all': 'All threads',
  'threads.close': 'Close thread',
  'threads.title': 'Threads',
  'threads.closeList': 'Close threads',
  'threads.cantRead': 'This message can’t be read on this device.',
  'threads.message': 'Message',
  'threads.empty': 'No threads here yet.',
  'threads.emptyLine': 'Point at a message and choose Reply in thread to start one.',
  'threads.emptyTouch': 'Tap a message and choose Reply in thread to start one.',
  'chat.replyInThread': 'Reply in thread',
  'accounts.opening': 'Opening {circle}…',
  'banner.section': 'Banner',
  'banner.failed': 'Couldn’t set the banner. {reason}',
  'banner.computer': 'Computer',
  'banner.phone': 'Phone',
  'banner.onComputer': 'Banner on a computer',
  'banner.onPhone': 'Banner on a phone',
  'delete.section': 'Delete',
  'delete.channel': 'Delete channel',
  'delete.channelLine': 'Deletes the channel and all its messages and files for everyone.',
  'delete.space': 'Delete space',
  'delete.spaceLine': 'Deletes the space for everyone, with its channels that are in no other space.',
  'delete.button': 'Delete…',
  'delete.titleNamed': 'Delete {name} for everyone?',
  'delete.titleChannel': 'Delete this channel for everyone?',
  'delete.titleSpace': 'Delete this space for everyone?',
  'delete.text': 'All messages and files in it are deleted for everyone. This can’t be undone.',
  'delete.goes': 'These channels are deleted with it:',
  'delete.stays': 'These stay, because they are in another space or you aren’t their admin:',
  'delete.subspaces': 'These spaces inside it stay and move out:',
  'delete.confirm': 'Delete for everyone',
  'delete.busy': 'Deleting…',
  'delete.slow': 'Still deleting… This can take a minute for a channel with many messages.',
  'delete.done': '{name} was deleted.',
  'delete.menuChannel': 'Delete channel…',
  'delete.menuSpace': 'Delete space…',
  'gone.space': 'This space was deleted.',
  'gone.channel': 'This channel was deleted.',
  'gone.chat': 'This chat was deleted.',
  'delete.doneMany': '{name} and {n} channels were deleted.',
  'delete.failed': 'Couldn’t delete. {reason}',
  'delete.partly': 'Some of it couldn’t be deleted. Try again.',
  'delete.notAdminChannel': 'You aren’t an admin of this channel any more.',
  'delete.notAdminSpace': 'You aren’t an admin of this space any more.',
  'delete.direct': 'Direct chats can only be left.',
  'delete.already': 'It is being deleted already.',
  'change.deleteChannel': '{by} deleted the channel {name}',
  'change.deleteSpace': '{by} deleted the space {name}',
  'circles.default': 'Default',
  'circles.more': 'More options for {circle}',
  'circles.add': 'Add a circle',
  'circles.note': 'New circles come by invitation. Ask the person who looks after a circle to invite {email}.',
  'circles.switch': 'Switch circle',
  'accounts.signedOut': 'Signed out — sign in again',
  'accounts.signOutAll': 'Sign out of all accounts',
  'accounts.newElsewhere': 'New messages in another account',
  'accounts.newMessages': { one: '1 new message', other: '{n} new messages' },
  'accounts.adding': 'Add an account',
  'accounts.allTitle': 'Sign out of all accounts?',
  'accounts.allLine': { one: 'Both accounts are signed out on this device, and their messages and keys stored here are deleted. Without a recovery key, encrypted chats can’t be read on a new device.', other: 'All {total} accounts are signed out on this device, and their messages and keys stored here are deleted. Without a recovery key, encrypted chats can’t be read on a new device.' },
  'accounts.allButton': 'Sign out of all',
  'accounts.signingOut': 'Signing out…',
  'accounts.otherTitle': 'In your other account',
  'accounts.otherLine': 'This conversation belongs to {address}.',
  'accounts.switchOpen': 'Switch and open it',
  'status.menu': 'Status',
  'status.invisible': 'Invisible',
  'status.onlineLine': 'Idle after 10 minutes away',
  'status.idleLine': 'Shown as away',
  'status.dndLine': 'No notifications',
  'status.invisibleLine': 'Shown as offline, you can still chat',
  'status.quietUntil': 'Quiet hours until {time}',
  'status.myAccount': '{name}, My account',
  'quiet.title': 'Quiet hours',
  'quiet.line': 'No notifications or sounds in the evenings and at weekends, on all your devices. Messages still arrive.',
  'quiet.now': 'Quiet now, until {time}. Messages still arrive — you see them when you look.',
  'quiet.from': 'From',
  'quiet.until': 'Until',
  'people.message': 'Message',
  'people.more': 'More',
  'people.block': 'Block',
  'people.unblock': 'Unblock',
  'people.blocked': 'Blocked',
  'people.blockedLine': 'Their messages and invites don’t reach you.',
  'people.banned': 'Banned',
  'people.invited': 'Invited',
  'people.by': 'By: {name}',
  'people.reason': 'Reason: {reason}',
  'people.noReason': 'No reason given.',
  'people.unban': 'Unban',
  'people.cancelInvite': 'Cancel invite',
  'people.moderation': 'Moderation',
  'people.reasonField': 'Reason',
  'people.invite': 'Invite',
  'people.remove': 'Remove',
  'people.ban': 'Ban',
  'people.tooManyChats': 'Too many new chats at once. Please try again in a minute.',
  'people.chatFailed': 'The chat with {name} couldn’t be started. Please try again.',
  'people.name': 'Name',
  'people.people': 'People',
  'people.youChatWith': 'People you chat with',
  'people.inCircle': 'People in {circle}',
  'people.typeName': 'Type a name to find someone.',
  'people.noneFound': 'No one found for “{text}”.',
  'people.directoryFailed': 'Couldn’t search the directory. Check your connection and try again.',
  'people.notInChat': 'Not in the chat yet',
  'people.notInChatMessage': '{name} hasn’t opened {product}’s chat yet. You can message them once they’ve signed in.',
  'people.notInChatInvite': '{name} hasn’t opened {product}’s chat yet. You can invite them once they’ve signed in.',
  'people.copyAddress': 'Copy address',
  'people.inviteTo': 'Invite to {name}',
  'people.whoInvite': 'Who do you want to invite?',
  'people.done': 'Done',
  'people.cantInvite': 'You can’t invite people to this channel.',
  'people.cantInviteSpace': 'You can’t invite people to this space.',
  'people.inviteFailed': '{name} couldn’t be invited. Please try again.',
  'people.invitePeople': 'Invite people',

  // 18 Channel and space settings
  'frame.spaceSettings': 'Space settings',
  'frame.changeBanner': 'Change banner…',
  'people.lowerTitle': 'Lower your own role?',
  'people.lowerText': 'You won’t be able to undo this yourself.',
  'people.lower': 'Lower role',
  'people.adminTitle': 'Make {name} an admin?',
  'people.adminText': '{name} will have the same rights as you.',
  'people.makeAdmin': 'Make admin',
  'people.managePermissions': 'Manage permissions',
  'roles.admin': 'Admin',
  'roles.moderator': 'Moderator',
  'roles.member': 'Member',
  'roles.muted': 'Muted',
  'roles.founder': 'Founder',
  'roles.level': 'Level {n}',
  'settings.general': 'General',
  'settings.userManagement': 'User management',
  'settings.members': 'Members',
  'settings.permissions': 'Permissions',
  'settings.profile': 'Profile',
  'settings.picture': 'Picture',
  'settings.upload': 'Upload',
  'presets.title': 'Choose a picture',
  'presets.pictures': 'Pictures',
  'presets.use': 'Use this picture',
  'presets.saving': 'Saving…',
  'settings.reset': 'Reset',
  'settings.remove': 'Remove',
  'settings.channelAccess': 'Channel access',
  'settings.channelAccessLine': 'Choose how people get into the channel.',
  'settings.spaceAccess': 'Space access',
  'settings.spaceAccessLine': 'Choose how people get into the space.',
  'settings.inviteOnly': 'Invite only',
  'settings.spaceMembers': 'Space members',
  'settings.anyone': 'Anyone',
  'settings.other': 'Other',
  'settings.history': 'Message history',
  'settings.historyLine': 'Who can read earlier messages — applies to messages sent from now on.',
  'settings.historyAll': 'Members, all messages',
  'settings.historyInvited': 'Members, since they were invited',
  'settings.historyJoined': 'Members, since they joined',
  'settings.encryption': 'Encryption',
  'settings.encryptedLine': 'Messages here are end-to-end encrypted.',
  'settings.notEncryptedLine': 'Once on, encryption can’t be turned off.',
  'settings.on': 'On',
  'settings.turnOn': 'Turn on',
  'settings.turnOnTitle': 'Turn on encryption?',
  'settings.turnOnText': 'Encryption can’t be turned off again. Search and previews of new messages work only on your own devices.',
  'settings.membersCount': { one: '{n} Member', other: '{n} Members' },
  'settings.searchMembers': 'Search members',
  'settings.results': { one: '{n} result', other: '{n} results' },
  'settings.joined': 'Joined',
  'settings.noMembers': 'No members',
  'settings.noInvited': 'No invited people',
  'settings.noBanned': 'No banned people',
  'settings.foundersLine': 'Founders may do everything. Who they are is fixed when the channel is made.',
  'settings.andAbove': '{role} and above',
  'settings.groupMessages': 'Messages',
  'settings.sendMessages': 'Send messages',
  'settings.sendStickers': 'Send stickers',
  'settings.sendReactions': 'Send reactions',
  'settings.mentionRoom': 'Mention @room',
  'settings.pinMessages': 'Pin messages',
  'settings.groupCalls': 'Calls',
  'settings.calls': 'Start or join calls',
  'settings.groupModeration': 'Moderation',
  'settings.removePeople': 'Remove people',
  'settings.deleteOthers': 'Delete others’ messages',
  'settings.deleteOwn': 'Delete own messages',
  'settings.groupOverview': 'Overview',
  'settings.groupSettings': 'Settings',
  'settings.changeAccess': 'Change who can join',
  'settings.changePermissions': 'Change permissions',
  'settings.turnOnEncryption': 'Turn on encryption',
  'settings.groupOther': 'Other',
  'settings.emojiPacks': 'Manage emoji & stickers',
  'settings.groupManage': 'Manage',
  'settings.manageChannels': 'Manage channels',
  'settings.groupSpaceOverview': 'Space overview',
  'settings.spacePicture': 'Space picture',
  'settings.spaceName': 'Space name',
  'settings.spaceTopic': 'Space topic',
  'settings.changeSpaceAccess': 'Change space access',
  'settings.changes': 'You have changes. Apply them when ready.',
  'settings.apply': 'Apply changes',
  'settings.notApplied': 'Your changes weren’t applied. {reason}',

  // 19 Security and sessions
  'auth.again': 'Sign in again',
  'auth.againLine': 'To do this, sign in again with single sign-on.',
  'auth.cantDo': '{product} can’t do the sign-in step the server asks for here.',
  'auth.continue': 'Continue',
  'auth.continueSso': 'Continue with SSO',
  'auth.password': 'Your password',
  'auth.passwordField': 'Password',
  'auth.passwordLine': 'To do this, enter your account password.',
  'auth.step': '(Step {i} of {n}.)',
  'auth.wrongPassword': 'That password isn’t right.',
  'auth.approve': 'Approve the reset',
  'auth.approveLine': 'Approve the reset on your account page, then come back here and press Continue.',
  'auth.openPage': 'Open account page',
  'auth.checking': 'Checking…',
  'auth.notApproved': 'The reset isn’t approved. Approve it on your account page, then press Continue again.',
  'security.accountPage': 'Your account page',
  'security.accountPageLine': 'You can see and sign out all your signed-in devices there.',
  'security.approveThere': 'Approve the reset on your account page, then come back here.',
  'security.backup': 'Encryption backup',
  'security.cantLoad': 'Your sessions can’t be loaded right now.',
  'security.confirm': 'Confirm',
  'security.connected': 'Connected',
  'security.create': 'Create',
  'security.createKey': 'Create recovery key',
  'security.createText': 'With your recovery key you can read your messages on a new phone or computer, even when this one is gone.',
  'security.createTitle': 'Create your recovery key',
  'security.creating': 'Creating…',
  'security.current': 'Current',
  'security.disconnected': 'Disconnected',
  'security.done': 'Done',
  'security.enterKey': 'Enter your recovery key.',
  'security.enterName': 'Enter a name.',
  'security.hide': 'Hide',
  'security.keepKey': 'Keep this key somewhere safe, like a password manager. You need it to read your messages when you sign in on a new device.',
  'security.lastActivity': 'Last activity: {day} {time}',
  'security.noActivity': 'No activity yet',
  'security.later': 'Later',
  'security.locked': 'Your earlier messages are locked on this device.',
  'security.lockedLine': 'Unlock them with your recovery key, or from another device where you’re signed in.',
  'security.namesVisible': 'Session names are visible to others.',
  'security.noBackup': 'There’s no backup on the server.',
  'security.notConfirmed': 'Not confirmed yet',
  'security.notConfirmedLine': 'Confirm it’s yours so it can read your messages too.',
  'security.open': 'Open',
  'security.options': 'Verification options',
  'security.others': 'Others',
  'security.protect': 'Protect your messages',
  'security.protectLine1': 'Only your devices can read your chats.',
  'security.protectLine2': 'Without a recovery key, your old messages are lost with this device.',
  'security.recoveryKey': 'Recovery key',
  'security.recoveryLine': 'Lets every device you sign in on read your messages.',
  'security.rename': 'Rename',
  'security.renameTitle': 'Rename session',
  'security.reset': 'Reset',
  'security.resetText': 'This can’t be undone. Everyone who verified you will see a security warning, and your encryption backup is lost. Only do this if you lost your recovery key and every device you could verify from.',
  'security.resetTitle': 'Reset device verification',
  'security.restore': 'Restore',
  'security.restoring': 'Restoring: {p}%',
  'security.saving': 'Saving…',
  'security.security': 'Security',
  'security.selected': 'Sign out of the selected sessions ({n} selected).',
  'security.sessionName': 'Session name',
  'security.show': 'Show',
  'security.signOut': 'Sign out',
  'security.signOutFailed': 'Couldn’t sign out of the selected sessions. {reason}',
  'security.signOutOf': 'Sign out {name}',
  'security.syncing': 'Syncing ({n})',
  'security.undo': 'Undo',
  'security.unlock': 'Unlock',
  'security.unlockTitle': 'Unlock with your recovery key',
  'security.unlocked': 'This device is unlocked and can read your messages.',
  'security.unlocking': 'Unlocking…',
  'security.unverified': 'Unverified',
  'security.useKey': 'Use recovery key',
  'security.verified': 'Verified',
  'security.wrongKey': 'That recovery key isn’t right.',
  'usersettings.account': 'Account',
  'usersettings.sessions': 'Sessions',
  'usersettings.version': 'Version: {version}',
  'usersettings.signOut': 'Sign out',
  'verify.accept': 'Accept',
  'verify.acceptThere': 'Accept the request on your other device.',
  'verify.accepted': 'You accepted the request.',
  'verify.accepting': 'Accepting…',
  'verify.askedLine': 'Another of your devices asks to verify this one. Accept to start.',
  'verify.cancelled': 'The verification was cancelled.',
  'verify.done': 'This device is now verified.',
  'verify.match': 'They match',
  'verify.noMatch': 'They don’t match',
  'verify.okay': 'Okay',
  'verify.sameEmoji': 'Check that your other device shows the same emoji, in the same order:',
  'verify.sameNumbers': 'Check that your other device shows the same numbers:',
  'verify.starting': 'Starting the emoji comparison…',
  'verify.title': 'Device verification',
  'verify.waitAccept': 'Waiting for it to be accepted…',
  'verify.waitOther': 'Waiting for your other device…',
  'frame.protect': 'Protect your messages',
  'frame.unlock': 'Unlock your messages',
  'frame.confirmDevices': 'Confirm your other devices',
  'security.nNotConfirmed': { one: '{n} device not confirmed', other: '{n} devices not confirmed' },
  'security.unlockAsk': 'Unlock your earlier messages',
  'security.unlockAskLine': 'Your earlier messages are locked on this device. Unlock them with your recovery key, or confirm this device from another one.',
  // 07 The phone's tab bar (v1.58.31)
  'tabbar.label': 'Tabs',
  'tabbar.chats': 'Chats',
  'tabbar.you': 'You',
  'tabbar.mentions': { one: '{name}, {n} mention', other: '{name}, {n} mentions' },
  'tabbar.unread': '{name}, {n} unread',

  // 05 Not found
  'notFound.title': 'This page doesn’t exist',
  'notFound.line': 'The link may be old or mistyped. Your chats are all still there.',

  // 06 Update notice
  'update.title': '{product} was updated',
  'update.line': 'Reload to use the new version',
  'update.lineVersion': 'Reload to use version {version}',
  'update.updating': 'Updating {product}…',
  'update.moment': 'This takes a moment',
  'update.busy': 'Updating…',

  'search.search': 'Search',
  'search.in': 'Search {place}',
  'search.noneCalled': 'No channels or people called “{text}”',
  'search.noChannels': 'No channels',
  'search.noChannelsLine': 'You don’t have any channels yet.',
  'search.messagesFor': 'Search messages for “{text}”',
  'search.hint': 'Type # for channels, @ for direct chats, * for spaces. Open this with {hotkey}, or / when not typing.',
  'search.ctrlK': 'Ctrl + K',
  'search.placeholder': 'Search for keyword',
  'search.enter': 'Enter',
  'search.clear': 'Clear',
  'search.filter': 'Filter',
  'search.everywhere': 'Everywhere',
  'search.removeRoom': '{name}: remove from the search',
  'search.emptyLine': 'Search by a word in the messages.',
  'search.nothing': 'Nothing found for “{term}”',
  // Branded empty states (02, v1.58.26)
  'empty.channelLine': 'Write the first message — everyone in {channel} will see it.',
  'empty.chatLine': 'Say hello to {name}.',
  'empty.searchLine': 'Try other words, or search everywhere.',
  'empty.searchEverywhere': 'Search everywhere',
  'search.resultsFor': 'Results for “{term}”',
  'search.open': 'Open',
  'search.markRead': 'Mark as read',
  'search.noNotifications': 'No notifications',
  'search.noNotificationsLine': 'New notifications will show here.',

  'settings.backToMenu': 'Back to settings',
  'settings.myAccount': 'My account',
  'settings.accountTitle': 'Account',
  'settings.app': 'App',
  'settings.language': 'Language',
  'settings.followBrowser': 'Follow the browser ({language})',
  'settings.dateTime': 'Date & time',
  'settings.clock24': '24-hour time',
  'settings.dateFormat': 'Date format',
  'settings.messageBox': 'Message box',
  'settings.enterNewline': 'Enter for new line',
  'settings.enterNewlineMac': '⌘ + Enter sends, Enter makes a new line.',
  'settings.enterNewlineLine': 'Ctrl + Enter sends, Enter makes a new line.',
  'settings.privateActivity': 'Private activity',
  'settings.privateActivityLine': 'Others don’t see when you’re typing or how far you’ve read.',
  'settings.hideMembership': 'Hide membership changes',
  'settings.hideProfile': 'Hide profile changes',
  'settings.noAutoMedia': 'Don’t load media automatically',
  'settings.linkPreviews': 'Link previews',
  'settings.editProfile': 'Edit profile',
  'settings.avatar': 'Avatar',
  'settings.displayName': 'Display name',
  'settings.undo': 'Undo',
  'settings.notPicture': 'Please choose a picture (for example a JPG or PNG).',
  'settings.pictureFailed': 'Your picture couldn’t be changed.',
  'settings.pictureNotRemoved': 'Your picture couldn’t be removed.',
  'settings.removePicture': 'Remove picture',
  'settings.removePictureText': 'Remove this picture?',
  'look.appearance': 'Appearance',
  'look.colours': 'Colours',
  'look.mode': 'Colour mode',
  'look.light': 'Light',
  'look.dark': 'Dark',
  'look.system': 'System',
  'look.accent': 'Accent colour',
  'look.custom': 'Custom colour',
  'look.red': 'Red',
  'look.yellow': 'Yellow',
  'look.green': 'Green',
  'look.teal': 'Teal',
  'look.blue': 'Blue',
  'look.indigo': 'Indigo',
  'look.purple': 'Purple',
  'look.coral': 'Coral',
  'look.contrast': 'Contrast',
  'look.low': 'Low',
  'look.grey': 'Grey',
  'look.calm': 'Calm',
  'look.normal': 'Normal',
  'look.high': 'High',
  'look.highest': 'Highest',
  'look.style': 'Colour style',
  'look.balanced': 'Balanced',
  'look.playful': 'Playful',
  'look.greyNoStyles': 'The grey accent has no colour styles.',
  'look.display': 'Display & text',
  'look.sample1': 'Sphinx of black quartz, judge my vow',
  'look.example': 'Example',
  'look.sample2': 'The quick brown fox jumped over the lazy dog',
  'look.messageSize': 'Message size',
  'look.groupSpacing': 'Message group spacing',
  'look.chatInput': 'Chat input',
  'look.sendButton': 'Show send message button',
  'keys.title': 'Keyboard shortcuts',
  'keys.navigation': 'Navigation',
  'keys.formatting': 'Formatting',
  'keys.or': 'or',
  'keys.search': 'Open search',
  'keys.list': 'Hide or show the channel list',
  'keys.close': 'Close a dialog, menu or the reply being written',
  'keys.send': 'Send',
  'keys.swaps': 'Settings → General → Enter for new line swaps these',
  'keys.newline': 'New line',
  'keys.editLast': 'Edit your last message',
  'keys.emptyBox': 'in an empty message box',
  'keys.paste': 'Paste a picture or file',
  'keys.bold': 'Bold',
  'keys.italic': 'Italic',
  'keys.list8': 'Bulleted list',
  'keys.code': 'Inline code',
  'keys.codeBlock': 'Code block',
  'about.title': 'About',
  'about.logo': 'desyr logo',
  'about.line': '{product} team chat',
  'about.privacy': 'Privacy',
  'about.terms': 'Terms',
  'about.imprint': 'Imprint',
  'people.blockedPeople': 'Blocked people',
  'people.nobodyBlocked': 'Nobody. Blocked people’s messages and invites don’t reach you.',
  'people.blockSomeone': 'Block someone',
  'people.blockLine': 'Their messages and invites won’t reach you. They aren’t told.',
  'people.whoBlock': 'Who do you want to block?',
  'people.blockFailed': '{name} couldn’t be blocked. Please try again.',

  'call.call': 'Call',
  'call.inThisCall': 'In this call',
  'call.count': '{n} in call',
  'call.more': '+{n} more',
  'call.fewer': 'Show fewer',
  'call.join': 'Join',
  'call.inAnother': 'You’re in another call. End it to join this one.',
  'call.notInRoom': 'You can’t join this call — you’re not in this channel.',
  'call.notAllowed': 'You can’t join calls here.',
  'call.noBrowser': 'This browser can’t make calls. Try a current version of Chrome, Edge, Firefox or Safari.',
  'call.noServer': 'Calls aren’t set up on your circle’s chat server yet.',
  'call.nobody': 'No one is here yet.',
  'call.mute': 'Mute microphone',
  'call.unmute': 'Unmute microphone',
  'call.cameraOff': 'Turn camera off',
  'call.cameraOn': 'Turn camera on',
  'call.hideChat': 'Hide chat',
  'call.showChat': 'Show chat',
  'call.chat': 'Chat',
  'call.closeChat': 'Close the chat',
  'call.end': 'End',
  'call.video': 'Video call',
  'call.voice': 'Voice call',
  'call.videoNotAllowed': 'Video call (not allowed in this room)',
  'call.voiceNotAllowed': 'Voice call (not allowed in this room)',
  'call.askAdmin': 'You can’t start or join calls here. Ask the channel’s admin to allow it: Settings → Permissions → Start or join calls → Member.',
  'call.endFirst': 'End your current call first',
  'call.answer': 'Answer',
  'call.decline': 'Decline',
  'call.tooSlow': 'The call is taking too long to connect. Check your connection and try again.',
  'call.unavailable': 'The call couldn’t connect. Calls aren’t available right now — try again in a moment.',
  'call.closed': 'The call didn’t connect. Try again in a moment.',
  'call.settings': 'Voice & video',
  'call.devicesLine': 'Microphone, speaker and camera are chosen in the call’s own settings during a call.',
  'call.processing': 'Voice processing',
  'call.noise': 'Noise suppression',
  'call.browser': 'Browser',
  'call.off': 'Off',
  'call.echo': 'Echo cancellation',
  'call.nextCall': 'Changes apply from the next call.',
  'call.calls': 'Calls',
  'call.service': 'Call service',
  'call.available': 'Calls available',
  'call.checking': 'Checking the call service…',
  'call.works': 'Calls work on this device.',
  'call.unreachable': 'The call service can’t be reached from this device right now. Calls may not work — check your connection, or ask your circle’s admin if it stays like this.',
  'call.notSetUp': 'Calls aren’t set up on your circle’s server yet.',

  'mail.connectTitle': 'Connect your account',
  'mail.connectLine': 'Enter the password of {address} once. desyr then stays connected to your circle account on this device.',
  'mail.code': 'Code from your authenticator app',
  'mail.connecting': 'Connecting…',
  'mail.needsPassword': 'Your circle account needs your password again on this device.',
  'mail.connect': 'Connect',
  'mail.enterPassword': 'Please enter your password.',
  'mail.enterCode': 'Please enter the 6-digit code.',
  'mail.wrongPassword': 'Wrong password.',
  'mail.wrongCode': 'That code didn’t work. Please try again.',
  'mail.unreachable': 'Your circle account can’t be reached right now. Please try again in a moment.',
  'recovery.title': 'Account recovery',
  'recovery.address': 'Recovery address',
  'recovery.addressLine': '{address}: a link to choose a new password goes there if you forget it. Your admin can’t see it.',
  'recovery.noAddress': 'None yet. Add a private address to get a link there if you forget your password.',
  'recovery.change': 'Change',
  'recovery.add': 'Add',
  'recovery.codes': 'Recovery codes',
  'recovery.codesLeft': '{n} of 8 left. Each works once, without any mail.',
  'recovery.noCodes': 'None left. Make new ones so you can always get back in.',
  'recovery.newCodes': 'New codes',
  'recovery.newCodesTitle': 'New recovery codes',
  'recovery.newCodesLine': 'You get 8 new codes; the old ones stop working.',
  'recovery.makeCodes': 'Make new codes',
  'recovery.making': 'Making…',
  'recovery.yourCodes': 'Your new recovery codes',
  'recovery.yourCodesLine': 'Keep them somewhere safe, away from this device. Each works once; the old ones no longer work.',
  'recovery.codesFor': 'Recovery codes for {address}. Each works once.',
  'recovery.done': 'Done',
  'recovery.addressDialogLine': 'A private address, not your work one: the link for a forgotten password goes there, and your admin can’t see it. Leave it empty to remove it.',
  'recovery.yourPassword': 'Your password',
  'recovery.enterPassword': 'Please enter your password.',
  'recovery.saving': 'Saving…',
  'twostep.section': 'Sign-in',
  'twostep.title': 'Two-step sign-in',
  'twostep.onLine': 'On: after your password, a code from your authenticator app.',
  'twostep.offLine': 'Off. Turn it on so a stolen password alone isn’t enough to get in.',
  'twostep.turnOn': 'Turn on',
  'twostep.turnOff': 'Turn off',
  'twostep.turningOn': 'Turning on…',
  'twostep.turningOff': 'Turning off…',
  'twostep.onDialogLine': 'After your password, desyr will ask for a 6-digit code from an authenticator app on your phone (for example Google Authenticator, Microsoft Authenticator or 2FAS). Add desyr to the app with this key:',
  'twostep.copyKey': 'Copy key',
  'twostep.openApp': 'Open in authenticator app',
  'twostep.code': 'Code from the app',
  'twostep.lostPhone': 'Lost your phone later? A recovery code turns two-step sign-in off again.',
  'twostep.codeDoesntFit': 'That code doesn’t fit. Check the time on your phone and type the code the app shows now.',
  'twostep.typePassword': 'Please type your password.',
  'twostep.notYourPassword': 'That isn’t your password.',
  'twostep.offTitle': 'Turn off two-step sign-in?',
  'twostep.offLine2': 'Your password alone will be enough to sign in again.',
  'twostep.wrongPasswordOrCode': 'That password or code isn’t right.',
  'twostep.onToast': 'Two-step sign-in is on',
  'twostep.offToast': 'Two-step sign-in is off',
  'apps.title': 'Calendar and contacts in other apps',
  'apps.line': 'Calendar and contacts apps on your phone and computer can use your work account directly.',
  'apps.server': 'Server',
  'apps.signInName': 'Sign-in name',
  'apps.caldav': 'Calendar (CalDAV)',
  'apps.carddav': 'Contacts (CardDAV)',
  'apps.findThemselves': 'Most apps find calendar and contacts by themselves from the server.',
  'apps.copy': 'Copy {label}',
  'apps.usePassword': 'Use your desyr password.',
  'apps.twoStepOn': 'Two-step sign-in is on: other apps can\'t ask for the code, so give each one an app password instead.',
  'apps.passwords': 'App passwords',
  'apps.none': 'No app passwords yet.',
  'apps.created': 'Created {date}',
  'apps.expires': 'Expires {date}',
  'apps.create': 'Create app password',
  'apps.createLine': 'An app password lets one app sign in without the code. It works until you revoke it.',
  'apps.name': 'Name',
  'apps.namePlaceholder': 'e.g. iPhone Calendar',
  'apps.giveName': 'Please give it a name.',
  'apps.createButton': 'Create',
  'apps.creating': 'Creating…',
  'apps.shownOnce': 'Here is the app password. It won’t be shown again.',
  'apps.password': 'App password',
  'apps.revoke': 'Revoke',
  'apps.revokeNamed': 'Revoke {name}',
  'apps.revokeTitle': 'Revoke “{name}”?',
  'apps.revokeLine': 'The app using it can’t sign in any more until it has a new one.',
  'apps.revoking': 'Revoking…',
  'apps.revoked': 'App password revoked',
  'company.title': 'Circle',
  'company.people': 'People',
  'company.emoji': 'Emoji & stickers',
  'company.unreachable': 'Circle settings can’t be reached right now.',
  'company.findPerson': 'Find a person',
  'company.addPerson': 'Add a person',
  'company.admin': 'Admin',
  'company.blocked': 'Blocked',
  'company.notSetUp': 'Not set up yet',
  'company.noRecovery': 'No recovery',
  'company.noRecoveryLine': 'Can’t reset a forgotten password: no recovery address or codes. They add them in Settings → My account → Account recovery.',
  'company.youLabel': 'you',
  'company.you': 'You',
  'company.options': 'Options for {name}',
  'company.olderUnreachable': 'Older accounts can’t be shown right now.',
  'company.peopleUnreachable': 'The circle’s accounts can’t be reached right now.',
  'company.changeName': 'Change name',
  'company.newLink': 'New set-up link',
  'company.makeAdmin': 'Make admin',
  'company.removeAdmin': 'Remove admin role',
  'company.signOutEverywhere': 'Sign out everywhere',
  'company.block': 'Block',
  'company.unblock': 'Unblock',
  'company.delete': 'Delete',
  'company.fullName': 'Full name',
  'company.address': 'Address',
  'company.adminSwitch': 'Admin (can manage the circle)',
  'company.add': 'Add',
  'company.adding': 'Adding…',
  'company.addLine': 'You get a set-up link to hand over. They choose their own password; their chat, calendar and files are ready when they sign in.',
  'company.nameAndAddress': 'Please give a name and an address.',
  'company.inUse': '“{name}” is already in the circle. Please choose another name.',
  'company.cantBeUsed': '“{name}” can’t be used — it’s kept for a former person or the system. Please choose another.',
  'company.theirLink': 'Their set-up link',
  'company.theirLinkLine': 'Give this link to them in person, by phone or in a message to them. They open it, choose their own password and sign in. It works once, for 7 days, and you never learn the password.',
  'company.setupLink': 'Set-up link',
  'company.linkFailed': 'A new link couldn’t be made.',
  'company.signOutTitle': 'Sign {name} out everywhere?',
  'company.signOutText': 'They are signed out of chat on all their devices (for example a lost phone) and can sign in again with their password. Calendar, contacts and files on those devices stay open until their password changes — for a lost or stolen device, block them instead. They get a mail saying you did this.',
  'company.signOut': 'Sign out',
  'company.blockTitle': 'Block {name}?',
  'company.blockText': 'They can’t sign in any more — chat, calendar, contacts and files — and are signed out on all their devices. Their messages, calendar, contacts and files stay, and you can unblock them any time. They get a mail.',
  'company.deleteTitle': 'Delete {name}?',
  'company.deleteText': 'The account is deleted with their calendar, contacts and files, and their chat account is closed. This can’t be undone. To stop someone signing in, block them instead.',
  'company.adminNow': '{name} is an admin now',
  'company.noAdminNow': '{name} is no admin any more',
  'company.isBlocked': '{name} is blocked',
  'company.canSignIn': '{name} can sign in again',
  'company.signedOut': '{name} is signed out everywhere',
  'company.deleted': '{name} was deleted',
  'company.nameChanged': 'Name changed',
  'company.roleFailed': 'The role couldn’t be changed.',
  'company.signOutFailed': 'Signing out didn’t work. Please try again.',
  'company.blockFailed': 'Blocking didn’t work.',
  'company.unblockFailed': 'Unblocking didn’t work.',
  'company.deleteFailed': 'Deleting didn’t work.',
  'company.giveName': 'Please give a name.',
  'company.nameFailed': 'The name couldn’t be changed.',
  'company.recentChanges': 'Recent changes',
  'company.show': 'Show',
  'company.all': 'All',
  'company.signIn': 'Sign-in',
  'company.nothingYet': 'Nothing of this kind yet.',
  'change.add': '{by} added {who}',
  'change.addAdmin': '{by} added {who} as an admin',
  'change.rename': '{by} renamed {who} to {name}',
  'change.makeAdmin': '{by} made {who} an admin',
  'change.removeAdmin': '{by} took the admin role from {who}',
  'change.signout': '{by} signed {who} out everywhere',
  'change.block': '{by} blocked {who}',
  'change.unblock': '{by} unblocked {who}',
  'change.delete': '{by} deleted {who}',
  'change.newLink': '{by} made a new set-up link for {who}',
  'change.setUp': '{who} set up their account',
  'change.resetLink': '{who} reset their forgotten password (a link to their recovery address)',
  'change.resetCode': '{who} reset their forgotten password (a recovery code)',
  'change.resetCodeOff': '{who} reset their forgotten password (a recovery code; two-step sign-in switched off)',
  'change.requestDomain': '{by} asked for the domain {d}',
  'change.domainProven': '{by} showed that {d} is the circle’s',
  'change.withdrawDomain': '{by} withdrew the request for {d}',
  'change.addDomain': '{by} added the domain {d}',
  'change.approveDomain': '{by} approved the domain {d}',
  'change.refuseDomain': '{by} refused the domain {d}',
  'change.emojiPack': '{by} set up the circle emoji',
  'change.circleProfile': '{by} changed the circle’s name or logo',
  'circle.badName': 'Please enter a name of up to 40 characters.',
  'circle.badPicture': 'That picture can’t be used. Please upload it again.',
  'change.freeBusyOn': '{who} lets colleagues see when they’re busy',
  'change.freeBusyOff': '{who} stopped letting colleagues see when they’re busy',
  'emojiAdmin.title': 'Your circle’s emoji',
  'emojiAdmin.line': 'Admins add emoji and stickers for everyone. People use these and can’t add their own.',
  'emojiAdmin.setUp': 'Set up circle emoji',
  'emojiAdmin.setUpLine': 'Everyone can use these in messages and reactions; only admins can change them. To add one: an image, a :shortcode:, and whether it is an emoji, a sticker or both.',
  'emojiAdmin.companyEmoji': 'Circle emoji',
  'emojiAdmin.pack': 'Pack',
  'emojiAdmin.name': 'Name',
  'emojiAdmin.usage': 'Image usage',
  'emojiAdmin.usageLine': 'The images are used as emoji, stickers or both.',
  'emojiAdmin.emoji': 'Emoji',
  'emojiAdmin.sticker': 'Sticker',
  'emojiAdmin.both': 'Both',
  'emojiAdmin.images': 'Images',
  'emojiAdmin.upload': 'Upload images',
  'emojiAdmin.uploadLine': 'Choose images to add.',
  'emojiAdmin.select': 'Select',
  'emojiAdmin.shortcode': 'Shortcode:',
  'emojiAdmin.description': 'Description:',
  'emojiAdmin.undo': 'Undo',
  'emojiAdmin.apply': 'Apply Changes',
  'emojiAdmin.applyFailed': 'Couldn’t apply the changes. Please try again.',
  'emojiAdmin.stickers': 'desyr stickers',
  'emojiAdmin.stickersLine': '62 stickers, Fluent Emoji 3D by Microsoft (MIT licence), for everyone. Stored on your circle’s own server; nothing is loaded from elsewhere.',
  'emojiAdmin.adding': 'Adding {n}/{total}',
  'emojiAdmin.stickersFailed': 'Couldn’t add them. {reason}',
  'setup.welcome': 'Welcome, {name}',
  'setup.line': 'Choose the password for {address}. Only you will know it — not even your admin.',
  'setup.password': 'Password',
  'setup.again': 'The same again',
  'setup.recovery': 'Recovery address (optional)',
  'setup.recoveryLine': 'A private address, not your work one. If you forget your password, the link to choose a new one goes there. Your admin can’t see it.',
  'setup.setPassword': 'Set my password',
  'setup.notSame': 'The two passwords aren’t the same.',
  'setup.privateAddress': 'The recovery address should be a private email address (not your work address).',
  'setup.expired': 'That didn’t work. The link may have expired — ask your admin for a new one.',
  'setup.done': 'You’re all set',
  'setup.doneLine': 'These recovery codes each let you choose a new password once if you forget it. Keep them somewhere safe, away from this device — printed or in a password manager.',
  'setup.saved': 'I saved my recovery codes',
  'setup.signIn': 'Sign in',
  'setup.signInLine': 'Sign in with {address} and your new password.',
  'setup.bad': 'This link doesn’t work',
  'setup.badLine': 'It was used already or is older than 7 days. Ask your circle’s admin for a new one.',
  'setup.cantCheck': 'Can’t check this link right now',
  'setup.cantCheckLine': 'Check your connection and try again.',
  'recover.title': 'Forgot your password?',
  'recover.line': 'A link goes to your private recovery address. Your admin isn’t involved.',
  'recover.workEmail': 'Work email',
  'recover.send': 'Send reset link',
  'recover.sending': 'Sending…',
  'recover.useCodeInstead': 'Use a recovery code instead',
  'recover.back': 'Back to sign in',
  'recover.sent': 'Check your recovery address',
  'recover.sentLine': 'If {address} has a recovery address, a link was just sent there; it works for an hour. Nothing arrived? Look in the spam folder, use a recovery code, or ask your circle’s admin — who can’t reset your password but can give you a fresh, empty account.',
  'recover.sendAgain': 'Send again',
  'recover.useCode': 'Use a recovery code',
  'recover.codeLine': 'One of the codes you saved when you set up your account.',
  'recover.code': 'Recovery code',
  'recover.newPassword': 'New password',
  'recover.lostPhone': 'I also lost the phone with my authenticator app — turn two-step sign-in off',
  'recover.change': 'Change password',
  'recover.linkInstead': 'Send a link to my recovery address instead',
  'recover.choose': 'Choose a new password',
  'recover.resetLine': 'For {address}. You’ll be signed out everywhere.',
  'recover.done': 'Password changed',
  'recover.doneLine': 'You were signed out everywhere. Sign in with {address} and your new password.',
  'recover.twoStepOff': 'Two-step sign-in is off now. Turn it on again in Settings → My account → Sign-in.',
  'recover.lastCode': 'That was your last recovery code.',
  'recover.badLine': 'It was used already or is older than an hour.',
  'recover.newLink': 'Send a new link',
  'recover.askAdmin': 'Please ask your circle’s admin for help.',
  'recover.enterEmail': 'Please enter your work email address.',
  'recover.enterEmailAndCode': 'Please enter your work email address and a recovery code.',
  'admin.noDomain': 'The circle has no domain yet. Add one first.',
  'admin.nameNeeded': 'Please give a name, and an address with only letters, digits, dots, - and _.',
  'admin.noSuchDomain': 'That domain isn’t the circle’s any more. Please reload the page.',
  'admin.nameInUse': 'Someone in the circle already has that name — please choose another.',
  'admin.nameTaken': 'That name is kept for a former person or the system — please choose another.',
  'admin.alreadySetUp': 'They have set up their account already.',
  'admin.ownRole': 'You can’t change your own role.',
  'admin.yourself': 'You can’t do that to your own account.',
  'admin.chatStepFailed': 'Calendar, contacts and files have the change, but chat doesn’t yet. Please try again.',
  'admin.chatStillOpen': 'Calendar, contacts and files are deleted, but their chat account is still open. Please try again.',
  'admin.tooShort': 'Please use at least 10 characters.',
  'admin.wrongPassword': 'Wrong password.',
  'admin.linkNotValid': 'This link doesn’t work any more. Please ask for a new one.',
  'admin.linkInUse': 'This link is being used right now. Please try again in a moment.',
  'admin.notAnAddress': 'Please enter a valid email address.',
  'admin.companyAddress': 'Please enter a private email address (not your work address).',
  'admin.wrongCode': 'That address or code isn’t right. (Each code works only once.)',
  'admin.mailRefused': 'The server didn’t accept this change. Check what you entered and try again.',
  'admin.passwordRefused': 'The server didn’t accept this password. Try a longer one, with more kinds of characters.',
  'recover.fewLeft': { one: 'Only {n} recovery code left.', other: 'Only {n} recovery codes left.' },

  'notify.page': 'Notifications',
  'notify.system': 'System',
  'notify.desktop': 'Desktop notifications',
  'notify.desktopLine': 'Show a notification on this computer when a message arrives.',
  'notify.title': 'Notifications',
  'notify.linePhone': 'Show a notification on this phone when a message arrives.',
  'notify.lineTablet': 'Show a notification on this tablet when a message arrives.',
  'notify.onPhone': 'Notifications on this phone',
  'notify.onTablet': 'Notifications on this tablet',
  'notify.blockedPhone': 'Notifications for desyr are blocked on this phone. Allow them in the settings for desyr, then open desyr again.',
  'notify.blockedTablet': 'Notifications for desyr are blocked on this tablet. Allow them in the settings for desyr, then open desyr again.',
  'notify.allow': 'Allow',
  'notify.blocked': 'Your browser blocks notifications for desyr. Allow them in the site settings (the icon beside the address), then reload.',
  'notify.noNotifications': 'This browser can’t show notifications.',
  'notify.soundTitle': 'Notification sound',
  'notify.soundLine': 'Play a sound when a message arrives.',
  'notify.allMessages': 'All messages',
  'notify.mentionsSection': 'Mentions',
  'notify.keywords': 'Keywords',
  'notify.direct': 'Direct messages',
  'notify.channels': 'Channels',
  'notify.mentionsYou': 'When someone mentions you',
  'notify.containsName': 'Contains your name (“{name}”)',
  'notify.everyone': 'When someone notifies everyone (@room)',
  'notify.keyword': 'Keyword',
  'notify.removeKeyword': 'Remove “{keyword}”',
  'notify.sound': 'Sound',
  'notify.silent': 'Silent',
  'notify.off': 'Off',
  'push.closed': 'When desyr is closed',
  'push.device': 'Notifications on this device',
  'push.line': 'Messages and reminders also reach this device when desyr isn’t open. Only that something arrived goes through desyr’s servers, never what it says. Quiet hours and Do Not Disturb count here too.',
  'push.blocked': 'Notifications are blocked for this site, so nothing can reach this device while desyr is closed. Allow them next to the address, then reload.',
  'push.iPhone': 'On iPhone and iPad, desyr can notify you once it’s on your Home Screen: Settings → About → Install desyr, then open it from there.',
  'push.settingUp': 'Setting up…',
  'push.finish': 'Finish setting up',
  'push.failed': 'This device couldn’t be set up for notifications right now. desyr tries again by itself — or switch this off and on to try now.',
  'remind.menu': 'Remind me…',
  'remind.change': 'Change reminder…',
  'remind.title': 'Remind me',
  'remind.line': 'desyr reminds you about this message.',
  'remind.youll': 'You’ll be reminded {when}.',
  'remind.in20': 'In 20 minutes',
  'remind.in1h': 'In 1 hour',
  'remind.tomorrowAt': 'Tomorrow at {time}',
  'remind.another': 'Another time',
  'remind.set': 'Set',
  'remind.cancelIt': 'Cancel the reminder',
  'remind.today': 'today at {time}',
  'remind.tomorrow': 'tomorrow at {time}',
  'remind.on': '{date} at {time}',
  'remind.titleIn': 'Reminder · {channel}',
  'remind.reminder': 'Reminder',
  'remind.leftChannel': 'A message in a channel you left',
  'remind.aMessage': 'a message',
  'remind.onlyOpen': 'The reminder is set, but it only reaches you while desyr is open.',
  'remind.tryAgain': 'Try again.',

  'emojiAdmin.setUpFailed': 'Couldn’t set up the circle emoji. {reason}',
  'recover.makeNew': 'Make new ones after signing in.',
  'saved.save': 'Save message',
  'saved.removeFrom': 'Remove from saved',
  'saved.scheduled': 'Scheduled',
  'saved.reminders': 'Reminders',
  'saved.reminderWhen': 'Reminder {when}',
  'saved.saved': 'Saved',
  'saved.inChannel': ' in #{name}',
  'saved.inPerson': ' in {name}',
  'saved.gone': 'This message can’t be shown any more.',
  'saved.left': 'From a chat you’ve left.',
  'saved.remove': 'Remove',
  'saved.deleted': 'This message was deleted.',
  'saved.cantRead': 'Encrypted message — this device can’t read it yet.',
  'saved.picture': 'Picture',
  'saved.video': 'Video',
  'saved.sound': 'Sound',
  'saved.file': 'File',
  'saved.sticker': 'Sticker',
  'saved.empty': 'Nothing saved yet',
  'saved.emptyLine': 'Choose Save message in a message’s menu to keep it here. Only you see this list, on every device you use.',
  'saved.to': 'To {chat}',
  'saved.toAChat': 'a chat',
  'saved.sends': 'sends {when}',
  'saved.cancelMessage': 'Cancel this message',
  'saved.saveFailed': 'Couldn’t save the message.',
  'saved.removeFailed': 'Couldn’t remove it.',
  'saved.searchEncrypted': 'Search encrypted chats',
  'saved.searchEncryptedLine': 'The server can’t search encrypted chats, so this device keeps a private index of them. It stays on this device and is deleted when you sign out or turn this off.',
  'later.button': 'Send later',
  'later.noFiles': 'Files can’t be scheduled; send them first',
  'later.line': 'The server sends it at that time, even while desyr is closed.',
  'later.mondayAt': 'Monday at {time}',
  'later.schedule': 'Schedule',
  'later.scheduled': 'Scheduled for {when}',
  'later.failed': 'Couldn’t schedule it.',
  'later.noEncryption': 'Couldn’t schedule it. Encryption isn’t ready on this device yet. Try again in a moment.',
  'later.cancelFailed': 'Couldn’t cancel it.',

  'voice.record': 'Record a voice message',
  'voice.recording': 'Recording a voice message',
  'voice.delete': 'Delete recording',
  'voice.send': 'Send voice message',
  'voice.sending': 'Sending…',
  'voice.play': 'Play voice message',
  'voice.pause': 'Pause voice message',
  'voice.retryPlay': 'Retry voice message',
  'voice.cantLoad': 'Couldn’t load it.',
  'voice.tooShort': 'That was too short to send. Hold on a little longer.',
  'voice.notSent': 'Couldn’t send the voice message.',
  'voice.retry': 'Retry',
  'voice.discard': 'Discard',
  'voice.deleted': 'Recording deleted.',
  'voice.notAllowed': 'desyr isn’t allowed to use your microphone. Allow it in your browser’s site settings (the icon next to the address), then try again.',
  'voice.noMic': 'No microphone was found. Connect one and try again.',
  'voice.busy': 'Your microphone is being used by another app. Close it and try again.',
  'voice.didntStart': 'Recording didn’t start. Check your microphone and try again.',
  'voice.safariHint': 'To stop being asked, allow the microphone for this site in Safari: page menu → Website Settings → Microphone → Allow.',
  'voice.message': 'Voice message',

  'legal.privacy': 'Privacy policy',
  'legal.terms': 'Terms of use',
  'legal.imprint': 'Imprint',
  'legal.legal': 'Legal',
  'legal.missing': 'This text isn’t published yet. Please look again soon.',
  'legal.otherLanguage': 'This page is only available in German for now.',
  'legal.openSource': 'Open-source components',
  'legal.openSourceLine': 'desyr is built with these open-source components. Each one is listed with its licence.',
  'legal.source': 'Source at version {version}',

  'install.app': 'App',
  'install.title': 'Install {product}',
  'install.installed': '{product} is installed',
  'install.install': 'Install',
  'install.installedLine': '{product} runs as an app on this device.',
  'install.availableLine': 'Put {product} in your Dock or Start menu: its own window, notifications and an unread number on its icon, and it opens without a network.',
  'install.iosLine': 'Add {product} to your Home Screen. It then opens full screen like an app, and only there can it send you notifications.',
  'install.safariLine': 'In Safari’s menu choose File → Add to Dock. {product} gets its own window and Dock icon.',
  'install.titleBarTip': 'Tip: click the ⌄ at the top right of the {product} window to show {product}’s own title bar.',
  'install.menuLine': 'Use the install icon at the right of the address bar, or “Install {product}” in the browser’s menu.',
  'install.unsupportedLine': 'This browser can’t install web apps. Open {product} in Chrome, Edge, Brave or Safari to install it.',
  'install.iosStep1': 'Tap Share',
  'install.iosWhere': '(in Safari at the bottom, on an iPad at the top; in Chrome in the address bar)',
  'install.iosStep2': 'Choose “Add to Home Screen”, then “Add”.',
  'install.iosStep3': 'Open {product} from your Home Screen and sign in there.',
  'install.macStep1': 'In the menu bar choose File → Add to Dock.',
  'install.macStep2': 'Click “Add”.',
  'install.macStep3': 'Open {product} from your Dock and sign in there.',
  'install.guideLine': 'Add {product} to your Home Screen: it opens full screen like an app, and only there can it send you notifications.',
  'install.guideStep1': 'Tap Share (or ⋯, then Share)',
  'install.guideStep3': 'Open {product} from your Home Screen.',
  'install.notNow': 'Not now',
  'install.done': '{product} is installed. Open it from your Dock, Start menu or Home Screen.',

  'invites.none': 'No invites',
  'invites.noneLine': 'Invitations from colleagues to a channel or space will show here.',
  'invites.invitation': 'Invitation',
  'invites.joined': 'Joined {name}',
  'invites.joinedNoName': 'Joined',
  'invites.open': 'Open',
  'invites.encrypted': 'Encrypted',
  'invites.direct': 'Direct message',
  'invites.space': 'Space',
  'invites.from': 'From: {name}',
  'invites.unknown': 'Unknown',
  'invites.reason': 'Reason: {reason}',
  // 07 The phone's four tabs: their contents (v1.58.31)
  'four.devices': 'Devices',
  // desyr ID (36)
  'id.signIn': 'Sign in',
  'id.line': 'One sign-in for all your circles.',
  'id.continue': 'Continue with desyr ID',
  'id.noId': 'No desyr ID yet? The person who looks after your circle invites you.',
  'id.jumpLine': 'You’ll sign in on desyr ID’s page with your passkey or password, then come right back.',
  'id.noPasskeyHere': 'No passkey on this device?',
  'id.signInHere': 'Sign in on this device',
  'id.hereLine': 'Lost your phone, or is your passkey only on a computer? We’ll email you a link to set up this device. Continuing with that link replaces all your passkeys and signs you out on every device. Afterwards, set up passkeys on your other devices again.',
  'id.passwordNoNeed': 'You sign in with a password? Then you don’t need this: choose “Continue with desyr ID” and enter it.',
  'id.emailMeLink': 'Email me a link',
  'id.phoneAtHand': 'Is your phone at hand? Choose “Continue with desyr ID” and scan the code with your phone (Bluetooth on) instead.',
  'id.checkEmail': 'Check your email',
  'id.linkOnItsWay': 'If {email} has a desyr ID, a link is on its way. Open it on this device within 30 minutes.',
  'id.backToSignIn': 'Back to sign-in',
  'id.setUpDevice': 'Set up this device',
  'id.setUpLine': 'This sets up this device for {email}. All your passkeys are removed and you’re signed out on every device. Afterwards, add your other devices again: on each one choose “Continue with desyr ID” and scan the code with your phone.',
  'id.settingUp': 'Setting up…',
  'id.checkEmailAgain': 'Check your email again',
  'id.passkeysGoing': 'Your old passkeys are being removed. A second email, “Set up your desyr ID”, arrives in a few minutes. Open it here, on this device, and choose Passkey.',
  'id.ownersNeedPasskey': 'Owners and admins of a circle need a passkey.',
  'id.linkUsed': 'This link has expired or was used already.',
  'id.askNewLink': 'Ask for a new link',
  'id.tooManyLinks': 'Too many links right now. Please try again in an hour.',
  'id.addPasskeyDevice': 'Add a passkey on this device',
  'id.devicePasskeyTitle': 'Sign in here without your phone',
  'id.devicePasskeyLine': 'If you signed in here by scanning a code with your phone, add a passkey on this device too.',
  'id.addPasskeyHere': 'Add a passkey here',
  'id.notNow': 'Not now',
  'id.unconfirmed': 'You’re signed out of your circles here, but desyr ID didn’t confirm it. On a shared device, finish signing out.',
  'id.finishSignOut': 'Finish signing out',
  'common.continue': 'Continue',
  'id.legacy': 'Sign in with your circle password instead',
  'id.failed': 'Signing in didn’t work right now. Please try again in a moment.',
  'id.tryAgain': 'Try again',
  'id.unavailable': 'desyr ID can’t be reached right now. Please try again in a moment.',
  'circles.tile': 'Your circles: {circle}',
  'id.circles': 'Your circles',
  'id.signedInAs': 'Signed in as {email}.',
  'id.signingIn': 'Signing in…',
  'id.ready': 'Ready',
  'id.couldntSignIn': 'Couldn’t sign in',
  'id.retry': 'Retry',
  'id.noCircle': 'You aren’t in a circle yet. Ask the person who looks after your circle to invite you.',
  'id.signOut': 'Sign out',
  'id.cantLoad': 'Your circles can’t be loaded right now.',
  'id.open': 'Open desyr',
  'id.opening': 'Opening your circles…',
  'id.passkeyNeeded': 'Needs your passkey',
  'id.signInAgain': 'Sign in again',
  'id.setDefault': 'Set as default',
  'id.signOutLine': 'You’ll be signed out of all your circles on this device.',
  'id.passkeys': 'Sign-in and passkeys',
  'id.manageId': 'Manage desyr ID',
  'id.manageLine': 'Your sign-in: email, password, passkeys and devices.',
  'id.newTab': ', opens in a new tab',
  'id.manage': 'Manage my desyr ID',
  // desyr ID (38)
  'id.pictureForAll': 'Your picture is the same in all your circles.',
  'id.keyForAll': 'This key works for all your circles.',
  'id.sameKey': 'Enter your recovery key — the same one for all your circles.',
  'id.keyReplaces': 'The new key replaces the old one in all your circles on this device.',
  // desyr ID (39)
  'id.leaveItem': 'Leave {circle}…',
  'id.leaveTitle': 'Leave {circle}?',
  'id.leaveText': 'Your messages in the channels you’re in disappear for everyone, and your files and direct chats in {circle} are deleted. Your desyr ID and your other circles stay.',
  'id.typeCircle': 'Type {circle} to confirm',
  'id.leave': 'Leave',
  'id.leavingNow': 'Leaving…',
  'id.ownsCircle': 'You own {circle}. Hand it over to an admin first.',
  'id.leavingToast': 'You’re leaving {circle}. This takes a few minutes.',
  'id.couldntLeave': 'Couldn’t leave. {reason}',
  'id.removedToast': 'You’re not in {circle} any more.',
  'id.deleteCard': 'Delete my desyr ID',
  'id.deleteLine': 'Leaves every circle and deletes your desyr ID for good.',
  'id.deleteButton': 'Delete my desyr ID…',
  'id.deleteTitle': 'Delete your desyr ID?',
  'id.deleteSeveral': 'You leave {n} circles: {names}.',
  'id.deleteOne': 'You leave {circle}.',
  'id.deleteRest': 'Your messages in the channels you’re in there disappear for everyone, your files and direct chats there are deleted, and your desyr ID with your passkeys, password and picture is deleted. This can’t be undone.',
  'id.deleteNone': 'Your desyr ID with your passkeys, password and picture is deleted. This can’t be undone.',
  'id.typeEmail': 'Type your email to confirm',
  'id.deleteForGood': 'Delete for good',
  'id.deleting': 'Deleting…',
  'id.couldntDelete': 'Couldn’t delete your desyr ID. {reason}',
  'id.beingDeleted': 'Your desyr ID is being deleted. This takes a few minutes.',
  'id.retiredLink': 'This link doesn’t work any more. Sign in with your desyr ID.',
  // desyr ID (37)
  'id.expiredLink': 'Invited, but the link in your email has expired? Ask the person who invited you to send the invitation again.',
  'id.invitations': 'Invitations',
  'id.invitedYou': '{inviter} invited you as {name}.',
  'id.join': 'Join {circle}',
  'id.joining': 'Joining…',
  'id.decline': 'Decline',
  'id.declineTitle': 'Decline the invitation to {circle}?',
  'id.confirmEmail': 'Confirm your email to see your invitations. The link is in the email that invited you.',
  'id.circleNeedsPasskey': '{circle} needs a passkey for admins.',
  'id.addPasskey': 'Add a passkey',
  'id.notOpen': 'This invitation isn’t open any more.',
  'id.notYours': 'This invitation is for another email.',
  'id.joined': 'Joined {circle}',
  'id.openCircle': 'Open',
  'id.invite': 'Invite someone',
  'id.email': 'Email',
  'id.emailHelp': 'Their own email. The invitation goes there; it also becomes their desyr ID if they don’t have one yet.',
  'id.nameIn': 'Name in {circle}',
  'id.nameHelp': 'How others find them in {circle}.',
  'id.role': 'Role',
  'id.owner': 'Owner',
  'id.admin': 'Admin',
  'id.member': 'Member',
  'id.adminHelp': 'Admins need a passkey. If they have none yet, they’re asked to add one when they join.',
  'id.language': 'Language of the invitation',
  'id.send': 'Send invitation',
  'id.sending': 'Sending…',
  'id.sent': 'Invitation sent to {email}',
  // The phone's sheet (v1.58.59)
  'id.findAt': 'How others find them in {circle}: {address}',
  'id.sevenDays': 'They have 7 days to accept.',
  'id.inviteAnother': 'Invite someone else',
  'id.done': 'Done',
  'id.nameRole': '{name} · {role}',
  'id.sentOn': 'Sent {date}',
  'id.openUntil': 'Open until {date}',
  'id.expired': 'Expired',
  'id.sendAgain': 'Send again',
  'id.revoke': 'Revoke',
  'id.revokeTitle': 'Revoke the invitation for {email}?',
  'id.revoked': 'Invitation revoked',
  'id.seats': '{used} of {total} seats used',
  'id.alreadyMember': '{email} is in {circle} already.',
  'id.alreadyInvited': '{email} is invited already.',
  'id.nameTaken': '“{name}” is taken, or was used before in {circle}. Please choose another one.',
  'id.noSeat': '{circle} has no free seat.',
  'id.badRole': 'Choose a role: member or admin.',
  'id.badLanguage': 'Choose German or English.',
  'id.badEmail': 'Please enter a valid email address.',
  'id.hostedEmail': 'Please use their own email — not an address of a circle or of desyr.',
  'id.tooManyOpen': '{circle} has 100 open invitations. Revoke some first.',
  'id.tooMany': 'Too many invitations right now. Please try again in a few minutes.',
  'id.busy': 'Joining is already under way. Please wait a moment.',
  'id.badName': 'Please choose another name: letters, digits, “.”, “_” or “-”.',
  'id.onlyOwner': 'Only the owner can do that.',
  'id.makeAdmin': 'Make admin',
  'id.makeMember': 'Make member',
  'id.needsPasskey': '{name} needs a passkey first. They can add one in Settings → My account.',
  'id.handOver': 'Hand over ownership…',
  'id.handOverTitle': 'Make {name} the owner of {circle}?',
  'id.stayAdmin': 'You stay an admin.',
  'id.handOverButton': 'Hand over',
  'id.remove': 'Remove from {circle}…',
  'id.removeTitle': 'Remove {name} from {circle}?',
  'id.removeText': 'Their messages in the channels they’re in disappear for everyone, and their files and direct chats in {circle} are deleted. Their desyr ID and their other circles stay.',
  'id.removeButton': 'Remove',
  'id.removing': '{name} is being removed.',
  'id.beingRemoved': 'Being removed…',
  'id.stillRemoving': 'Still being removed. This can take a while.',
  'id.checkAgain': 'Check again',
  'id.youManage': 'You manage {circle}. A passkey (your fingerprint or face) keeps it safe; admins need one.',
  'id.confirmPasskey': 'Confirm with your passkey to manage {circle}.',
  'id.stepInvite': 'To invite, confirm it’s you',
  'id.stepChange': 'To make this change, confirm it’s you',
  'id.stepLine': 'desyr ID opens to check your passkey and brings you back here.',
  'id.stepConfirm': 'Confirm with passkey',
  'id.confirm': 'Confirm',
  'id.passkeyFirst': 'Add a passkey first. Then confirm again.',
  // desyr ID: sign out everywhere (36, 37; v1.58.7)
  'id.everywhere': 'Sign out everywhere…',
  'id.everywhereTitle': 'Sign out everywhere?',
  'id.everywhereText': 'You’re signed out of all your circles and of your desyr ID on every device, this one too. You can sign in again with your passkey or password.',
  'id.everywhereDone': 'Signed out on all devices',
  'id.everywherePartly': 'Signed out of all circles, but your desyr ID couldn’t be signed out everywhere. Change your password to be safe.',
  'id.everywhereIncomplete': 'Not signed out everywhere yet. Try again.',
  'id.ok': 'OK',
  'id.noPasskey': 'No passkey',
  'id.noPasskeyTip': 'They can add one in Settings → My account.',
  'id.signOutText': 'They’re signed out of {circle} on all their devices (for example a lost phone) and can sign in again.',
} as const satisfies Record<string, string | { one: string; other: string }>;

export type Key = keyof typeof en;
